import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tag, X, Search, Loader2 } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

function BrandsSelector(props) {
  var selectedParentBrands = props.selectedParentBrands || [];
  var selectedSubBrands = props.selectedSubBrands || [];
  var onToggleParentBrand = props.onToggleParentBrand;
  var onToggleSubBrand = props.onToggleSubBrand;

  var parentBrandsState = useState([]);
  var parentBrands = parentBrandsState[0];
  var setParentBrands = parentBrandsState[1];

  var subBrandsState = useState([]);
  var subBrands = subBrandsState[0];
  var setSubBrands = subBrandsState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var parentSearchState = useState('');
  var parentSearch = parentSearchState[0];
  var setParentSearch = parentSearchState[1];

  var subSearchState = useState('');
  var subSearch = subSearchState[0];
  var setSubSearch = subSearchState[1];

  useEffect(function() {
    var token = localStorage.getItem('token');
    var headers = { Authorization: 'Bearer ' + token };
    Promise.all([
      axios.get(API + '/masters/brands', { headers: headers }),
      axios.get(API + '/masters/sub-brands', { headers: headers })
    ]).then(function(results) {
      setParentBrands(results[0].data.brands || []);
      setSubBrands(results[1].data.sub_brands || []);
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }, []);

  var activeParent = parentBrands.filter(function(b) { return b.is_active !== false; });
  var activeSub = subBrands.filter(function(b) { return b.is_active !== false; });

  var filteredParent = activeParent.filter(function(b) {
    if (!parentSearch) return true;
    return b.name.toLowerCase().includes(parentSearch.toLowerCase());
  });

  var filteredSub = activeSub.filter(function(b) {
    if (!subSearch) return true;
    return b.name.toLowerCase().includes(subSearch.toLowerCase()) ||
      (b.parent_brand_name || '').toLowerCase().includes(subSearch.toLowerCase());
  });

  if (loading) {
    return React.createElement('div', { className: 'flex items-center gap-2 py-4 text-sm text-slate-400' },
      React.createElement(Loader2, { size: 14, className: 'animate-spin' }),
      'Loading brands...'
    );
  }

  return React.createElement('div', { className: 'space-y-5', 'data-testid': 'brands-selector' },
    // ===== PARENT BRANDS =====
    React.createElement('div', { className: 'space-y-3' },
      React.createElement('div', { className: 'flex items-center gap-2 pb-2 border-b border-slate-200' },
        React.createElement(Tag, { size: 16, className: 'text-blue-600' }),
        React.createElement('h3', { className: 'font-semibold text-sm text-slate-700 uppercase tracking-wide' }, 'Parent Brands Handled')
      ),

      // Selected parent brands
      selectedParentBrands.length > 0 && React.createElement('div', { className: 'flex flex-wrap gap-1.5' },
        selectedParentBrands.map(function(name) {
          return React.createElement(Badge, {
            key: name,
            className: 'gap-1 bg-blue-100 text-blue-800 border-blue-200 cursor-pointer hover:bg-blue-200',
            onClick: function() { onToggleParentBrand(name); },
            'data-testid': 'selected-parent-brand-' + name
          }, name, React.createElement(X, { size: 12 }));
        })
      ),

      // Search
      React.createElement('div', { className: 'relative' },
        React.createElement(Search, { className: 'absolute left-3 top-1/2 -translate-y-1/2 text-slate-400', size: 14 }),
        React.createElement(Input, {
          value: parentSearch,
          onChange: function(e) { setParentSearch(e.target.value); },
          placeholder: 'Search parent brands...',
          className: 'pl-8 h-9 text-sm',
          'data-testid': 'parent-brand-search'
        })
      ),

      // Dropdown
      React.createElement('div', { className: 'border rounded-lg max-h-36 overflow-y-auto bg-white shadow-sm' },
        filteredParent.length > 0
          ? filteredParent.map(function(brand) {
              var isSelected = selectedParentBrands.includes(brand.name);
              return React.createElement('div', {
                key: brand.id,
                className: 'flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors ' + (isSelected ? 'bg-blue-50' : ''),
                onClick: function() { onToggleParentBrand(brand.name); },
                'data-testid': 'parent-brand-option-' + brand.name
              },
                React.createElement('input', { type: 'checkbox', checked: isSelected, readOnly: true, className: 'rounded border-slate-300 text-blue-600' }),
                React.createElement('span', { className: 'text-sm' }, brand.name)
              );
            })
          : React.createElement('div', { className: 'p-3 text-center text-sm text-slate-400' },
              'No parent brands found. Add in Admin Settings.'
            )
      )
    ),

    // ===== SUB BRANDS =====
    React.createElement('div', { className: 'space-y-3' },
      React.createElement('div', { className: 'flex items-center gap-2 pb-2 border-b border-slate-200' },
        React.createElement(Tag, { size: 16, className: 'text-teal-600' }),
        React.createElement('h3', { className: 'font-semibold text-sm text-slate-700 uppercase tracking-wide' }, 'Sub Brands Handled')
      ),

      // Selected sub brands
      selectedSubBrands.length > 0 && React.createElement('div', { className: 'flex flex-wrap gap-1.5' },
        selectedSubBrands.map(function(name) {
          return React.createElement(Badge, {
            key: name,
            className: 'gap-1 bg-teal-100 text-teal-800 border-teal-200 cursor-pointer hover:bg-teal-200',
            onClick: function() { onToggleSubBrand(name); },
            'data-testid': 'selected-sub-brand-' + name
          }, name, React.createElement(X, { size: 12 }));
        })
      ),

      // Search
      React.createElement('div', { className: 'relative' },
        React.createElement(Search, { className: 'absolute left-3 top-1/2 -translate-y-1/2 text-slate-400', size: 14 }),
        React.createElement(Input, {
          value: subSearch,
          onChange: function(e) { setSubSearch(e.target.value); },
          placeholder: 'Search sub brands...',
          className: 'pl-8 h-9 text-sm',
          'data-testid': 'sub-brand-search'
        })
      ),

      // Dropdown
      React.createElement('div', { className: 'border rounded-lg max-h-36 overflow-y-auto bg-white shadow-sm' },
        activeSub.length > 0
          ? (filteredSub.length > 0
              ? filteredSub.map(function(brand) {
                  var isSelected = selectedSubBrands.includes(brand.name);
                  return React.createElement('div', {
                    key: brand.id,
                    className: 'flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors ' + (isSelected ? 'bg-teal-50' : ''),
                    onClick: function() { onToggleSubBrand(brand.name); },
                    'data-testid': 'sub-brand-option-' + brand.name
                  },
                    React.createElement('input', { type: 'checkbox', checked: isSelected, readOnly: true, className: 'rounded border-slate-300 text-teal-600' }),
                    React.createElement('div', null,
                      React.createElement('span', { className: 'text-sm' }, brand.name),
                      brand.parent_brand_name && React.createElement('span', { className: 'text-xs text-slate-400 ml-2' }, '(' + brand.parent_brand_name + ')')
                    )
                  );
                })
              : React.createElement('div', { className: 'p-3 text-center text-sm text-slate-400' }, 'No matching sub brands')
            )
          : React.createElement('div', { className: 'p-3 text-center text-sm text-slate-400' },
              'No sub brands found. Add in Admin Settings.'
            )
      )
    )
  );
}

export default BrandsSelector;
